import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { SessionTimesService } from '../services/session-times.service';
import * as SessionTimeActions from './session-times.actions';

@Injectable()
export class SessionTimesEffects {
    private _actions$ = inject(Actions);
    private _store = inject(Store);
    private logger = inject(LoggerService);
    private _sessionTimeService = inject(SessionTimesService);
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting all session times',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All SessionTimes
    getAllSessionTimes$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SessionTimeActions.loadSessionTimes),
            switchMap(() => {
                return this._sessionTimeService.getAll().pipe(
                    map((res) => {
                        return SessionTimeActions.loadSessionTimesSuccess({
                            sessionTimes: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            SessionTimeActions.loadSessionTimesFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Get One SessionTime
    getSessionTime$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SessionTimeActions.loadSessionTime),
            switchMap((action) => {
                return this._sessionTimeService.getOne(action.id).pipe(
                    map((res) => {
                        return SessionTimeActions.loadSessionTimeSuccess({
                            sessionTime: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            SessionTimeActions.loadSessionTimeFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Create SessionTime
    createSessionTime$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SessionTimeActions.createSessionTime),
            switchMap((action) => {
                return this._sessionTimeService
                    .create(action.sessionTimeData, action.playdateId)
                    .pipe(
                        map((res) => {
                            return SessionTimeActions.createSessionTimeSuccess({
                                sessionTime: res.data,
                                success: 'Session time created successfully',
                            });
                        }),
                        catchError((error) =>
                            of(
                                SessionTimeActions.createSessionTimeFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Update SessionTime
    updateSessionTime$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SessionTimeActions.updateSessionTime),
            switchMap((action) => {
                return this._sessionTimeService
                    .update(action.sessionTimeData, action.id)
                    .pipe(
                        map((res) => {
                            return SessionTimeActions.updateSessionTimeSuccess({
                                sessionTime: res.data,
                                success: 'Session time updated successfully',
                            });
                        }),
                        catchError((error) =>
                            of(
                                SessionTimeActions.updateSessionTimeFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Delete SessionTime
    deleteSessionTime$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SessionTimeActions.deleteSessionTime),
            switchMap((action) => {
                return this._sessionTimeService.delete(action.id).pipe(
                    map(() => {
                        return SessionTimeActions.deleteSessionTimeSuccess({
                            id: action.id,
                            success: 'Session time deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            SessionTimeActions.deleteSessionTimeFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onSessionTimeActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SessionTimeActions.createSessionTimeSuccess,
                    SessionTimeActions.updateSessionTimeSuccess,
                    SessionTimeActions.deleteSessionTimeSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onSessionTimeActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SessionTimeActions.loadSessionTimesFailure,
                    SessionTimeActions.loadSessionTimeFailure,
                    SessionTimeActions.createSessionTimeFailure,
                    SessionTimeActions.updateSessionTimeFailure,
                    SessionTimeActions.deleteSessionTimeFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
